import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from "@angular/core";

import { ClassToggleService, HeaderComponent, INavData } from "@coreui/angular";
import { Subscription } from "rxjs";

import { environment } from "src/environments/environment";

import {
  AuthApiService,
  HeaderNavService,
  InactiveCheckService,
  PromptUpdateService,
} from "src/app/core/services";

export interface DropdownMenuActions {
  label: string;
  action: () => void;
  icon?: string;
}

interface NavItemInfo {
  nav_item_id: string;
  url: string;
  name: string;
  icon: string;
  breadcrumb: string;
}

@Component({
  selector: "app-base-layout-header",
  templateUrl: "./base-layout-header.component.html",
  styles: [
    `
      .session-warning-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1080;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .session-warning-modal {
        background: #fff;
        border-radius: 0.5rem;
        padding: 1.5rem;
        max-width: 420px;
        width: 90%;
        box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.3);
      }
    `,
  ],
})
export class BaseLayoutHeaderComponent
  extends HeaderComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() sidebarId: string = "sidebar";
  @Input() headerNavItems: INavData[] = [];
  @Input() dropdownMenuActions: DropdownMenuActions[] = [];
  @Input() userName: string;
  @Input() tmLogout = 0;

  /** Aviso de expiração de sessão (contagem regressiva). */
  public showLogoutWarning = false;
  public warningSecondsLeft = 0;
  private warningWindow = 0;
  private warningTimer: any = null;
  /** Agenda a abertura do aviso no instante exato (faltando warningWindow). */
  private warningOpenTimer: any = null;

  @ViewChild("navContainer", { read: ElementRef }) navContainerRef: ElementRef;
  @ViewChildren("navItemEl", { read: ElementRef })
  navItemRefs: QueryList<ElementRef>;

  private inactiveCheck: Subscription;

  protected appVersion: string;

  protected updater: PromptUpdateService;
  protected authService: AuthApiService;
  protected inactiveCheckService: InactiveCheckService;
  protected headerNavService: HeaderNavService;

  public backgroundTitleCls: string | null = null;

  /** Overflow - dropdown "Mais" */
  public visibleItemCount = 999;
  public overflowNavItems: INavData[] = [];
  private itemWidths: number[] = [];

  /** Favoritos - estado do dropdown */
  public availableNavItems: NavItemInfo[] = [];
  public filteredNavItems: NavItemInfo[] = [];
  public favoriteNavItemIds: Set<string> = new Set();
  public favSearchTerm = "";

  constructor(
    private classToggler: ClassToggleService,
    protected injector: Injector,
  ) {
    super();

    this.appVersion = environment.version;
  }

  ngOnInit(): void {
    this.authService = this.injector.get(AuthApiService);
    this.inactiveCheckService = this.injector.get(InactiveCheckService);
    this.updater = this.injector.get(PromptUpdateService);
    this.headerNavService = this.injector.get(HeaderNavService);

    if (!environment.production) {
      this.backgroundTitleCls = "debug-alert-header-color";
    } else if (environment.prefix.endsWith("-qa")) {
      this.backgroundTitleCls = "qa-alert-header-color";
    }

    this.startInactiveCheck();
  }

  ngAfterViewInit(): void {
    this.measureItemWidths();
    this.onWindowResize = this.onWindowResize.bind(this);
    window.addEventListener("resize", this.onWindowResize);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.headerNavItems) {
      this.measureItemWidths();
    }
    // tmLogout chega com o default (15min) e só depois é atualizado com o
    // session_time real (userData async no pai). Reconfigura ao mudar.
    if (changes.tmLogout && !changes.tmLogout.firstChange) {
      this.startInactiveCheck();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener("resize", this.onWindowResize);
    if (this.inactiveCheck) {
      this.inactiveCheck.unsubscribe();
    }
    this.clearWarningTimer();
    this.clearWarningOpenTimer();
  }

  private onWindowResize(): void {
    this.recalculateVisibleItems();
  }

  private measureItemWidths(): void {
    this.visibleItemCount = this.headerNavItems.length;
    setTimeout(() => {
      if (!this.navItemRefs) return;
      this.itemWidths = [];
      this.navItemRefs.forEach((ref) => {
        this.itemWidths.push(ref.nativeElement.offsetWidth);
      });
      this.recalculateVisibleItems();
    });
  }

  private recalculateVisibleItems(): void {
    if (!this.navContainerRef || this.itemWidths.length === 0) return;

    const navEl = this.navContainerRef.nativeElement;
    const parent = navEl.parentElement;
    if (!parent) return;

    // Mede espaço disponível a partir do pai, subtraindo largura dos irmãos
    let siblingsWidth = 0;
    for (const child of Array.from(parent.children) as HTMLElement[]) {
      if (child !== navEl && child.offsetWidth) {
        siblingsWidth += child.offsetWidth;
      }
    }

    // 120px reservado para botão estrela + dropdown "Mais" (dentro do nav container)
    const reservedWidth = 120;
    const availableWidth = parent.offsetWidth - siblingsWidth - reservedWidth;

    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < this.itemWidths.length; i++) {
      totalWidth += this.itemWidths[i];
      if (totalWidth > availableWidth) break;
      count = i + 1;
    }

    this.visibleItemCount = count;
    this.overflowNavItems = this.headerNavItems.slice(count);
  }

  private startInactiveCheck(): void {
    if (this.tmLogout <= 0) {
      return;
    }
    // Janela de aviso: 2 min (≥ 2 ciclos de checagem de 60s garantem o
    // aviso), limitada a metade do tempo de sessão.
    this.warningWindow = Math.min(2 * 60 * 1000, this.tmLogout / 2);
    // Idempotente: assina uma única vez; pode ser re-chamado quando tmLogout
    // muda para reconfigurar o keepalive com o session_time real.
    if (!this.inactiveCheck) {
      this.inactiveCheck = this.inactiveCheckService
        .event()
        .subscribe((time) => this.onInactivity(time));
      // Backend já expirou a sessão (keepalive 401) → desloga.
      this.inactiveCheck.add(
        this.inactiveCheckService.expired().subscribe(() => this.logout()),
      );
    }
    this.inactiveCheckService.start(this.tmLogout);
  }

  private onInactivity(time: number): void {
    this.scheduleWarning(time);
  }

  /**
   * Decide o aviso a partir da inatividade atual. Como o serviço só amostra a
   * cada 60s, abrir o aviso diretamente nesse evento faria o countdown começar
   * num ponto variável dentro da janela. Por isso, quando ainda falta mais que
   * warningWindow, agenda a abertura para o instante EXATO em que faltará
   * warningWindow — revalidando a inatividade real no disparo (cross-tab pode
   * ter renovado). Assim o countdown começa sempre com o mesmo valor.
   */
  private scheduleWarning(time: number): void {
    this.clearWarningOpenTimer();
    const remaining = this.tmLogout - time;

    if (remaining <= 0) {
      this.closeLogoutWarning();
      this.logout();
      return;
    }

    if (remaining <= this.warningWindow) {
      if (!this.showLogoutWarning) {
        this.openLogoutWarning(remaining);
      }
      return;
    }

    // Ainda fora da janela de aviso.
    if (this.showLogoutWarning) {
      // Atividade voltou antes de expirar → fecha o aviso.
      this.closeLogoutWarning();
    }
    this.warningOpenTimer = setTimeout(() => {
      this.inactiveCheckService
        .peekInactivity()
        .then((t) => this.scheduleWarning(t));
    }, remaining - this.warningWindow);
  }

  private openLogoutWarning(remaining: number): void {
    this.showLogoutWarning = true;
    // Pausa a renovação por presença: só "Continuar conectado" (keepalive
    // confirmado) ou atividade em outra aba renovam enquanto o aviso está
    // aberto — um clique no próprio modal não deve mascarar falha de keepalive.
    this.inactiveCheckService.pauseTracking();
    // Capado à janela: garante um valor inicial consistente mesmo se aberto
    // por um check de 60s (e não pelo agendamento preciso).
    this.warningSecondsLeft = Math.ceil(
      Math.min(remaining, this.warningWindow) / 1000,
    );
    this.warningTimer = setInterval(() => {
      this.warningSecondsLeft--;
      if (this.warningSecondsLeft <= 0) {
        this.clearWarningTimer();
        // Relê a inatividade compartilhada: outra aba pode ter registrado
        // atividade depois do aviso abrir — nesse caso não desloga.
        this.inactiveCheckService.peekInactivity().then((time) => {
          if (time > this.tmLogout) {
            this.logout();
          } else {
            this.closeLogoutWarning();
          }
        });
      }
    }, 1000);
  }

  private closeLogoutWarning(): void {
    this.clearWarningTimer();
    this.showLogoutWarning = false;
    this.inactiveCheckService.resumeTracking();
  }

  private clearWarningTimer(): void {
    if (this.warningTimer) {
      clearInterval(this.warningTimer);
      this.warningTimer = null;
    }
  }

  private clearWarningOpenTimer(): void {
    if (this.warningOpenTimer) {
      clearTimeout(this.warningOpenTimer);
      this.warningOpenTimer = null;
    }
  }

  continuarConectado(): void {
    // Só fecha o aviso após o backend confirmar a renovação (204). Em 401
    // (sessão já expirada) desloga; em falha transitória mantém o aviso e a
    // contagem regressiva seguem para o usuário tentar de novo.
    this.inactiveCheckService.keepAliveNow().subscribe({
      next: () => this.closeLogoutWarning(),
      error: (err) => {
        if (err?.status === 401) {
          this.logout();
        }
      },
    });
  }

  logout(): void {
    this.closeLogoutWarning();
    this.authService.logout().subscribe(() => {
      // this.router.navigate(['/login']);
    });
  }

  checkForUpdate(): void {
    this.updater.checkForUpdate();
  }

  applyUpdate(): void {
    this.updater.applyUpdate();
  }

  onFavDropdownOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.availableNavItems = this.headerNavService.getAvailableNavItems();
      this.favoriteNavItemIds =
        this.headerNavService.getCurrentFavoriteNavItemIds();
      this.favSearchTerm = "";
      this.filteredNavItems = this.availableNavItems;
    }
  }

  filterNavItems(): void {
    const term = this.favSearchTerm.toLowerCase();
    this.filteredNavItems = term
      ? this.availableNavItems.filter((i) =>
          i.breadcrumb.toLowerCase().includes(term),
        )
      : this.availableNavItems;
  }

  toggleFavorite(item: NavItemInfo, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.favoriteNavItemIds.has(item.nav_item_id)) {
      this.headerNavService.removeFavoriteByNavItemId(item.nav_item_id);
      this.favoriteNavItemIds.delete(item.nav_item_id);
    } else {
      if (this.favoriteNavItemIds.size >= 10) return;
      this.headerNavService.addFavorite(item).subscribe(() => {
        this.favoriteNavItemIds.add(item.nav_item_id);
      });
    }
  }

  isFavorite(navItemId: string): boolean {
    return this.favoriteNavItemIds.has(navItemId);
  }
}
